import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Megaphone, Send, Mail, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SEGMENTS = [
  { value: 'ALL', label: 'All Clients' },
  { value: 'BOARDED', label: 'Boarded with us' },
  { value: 'BOARD_AND_TRAIN', label: 'Board & Train customers' },
  { value: 'VIP', label: 'VIP Members' },
];

const buildEmailHtml = (a) => `
<table style="width:100%;max-width:600px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;color:#0B1220;">
  <tr><td style="background:#1B3A87;padding:24px;text-align:center;color:#fff;">
    <div style="font-weight:bold;font-size:20px;">Joint Forces K9</div>
    <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#C8102E;">K9os App</div>
  </td></tr>
  <tr><td style="padding:24px;background:#fff;">
    <h1 style="margin:0 0 12px;font-size:22px;color:#0B1220;">${(a.title || '').replace(/</g, '&lt;')}</h1>
    <div style="font-size:14px;line-height:1.6;color:#334155;white-space:pre-wrap;">${(a.body || '').replace(/</g, '&lt;')}</div>
    ${a.cta_label && a.cta_url ? `<div style="margin-top:20px;"><a href="${a.cta_url}" style="display:inline-block;background:#C8102E;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;font-weight:bold;">${a.cta_label}</a></div>` : ''}
  </td></tr>
  <tr><td style="padding:16px;font-size:11px;color:#94a3b8;text-align:center;background:#0B1220;color:#cbd5e1;">
    Joint Forces K9 · https://k9os.app
  </td></tr>
</table>`;

export default function AdminMarketing() {
  const [list, setList] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('Book Now');
  const [ctaUrl, setCtaUrl] = useState('/book');
  const [audience, setAudience] = useState('ALL');
  const [segment, setSegment] = useState('ALL');
  const [emailReady, setEmailReady] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => api.get('/admin/announcements').then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and body required'); return; }
    setSending(true);
    try {
      const aud = audience === 'SEGMENT' || segment !== 'ALL' ? 'SEGMENT' : 'ALL';
      await api.post('/admin/announcements', {
        title, body, cta_label: ctaLabel || null, cta_url: ctaUrl || null,
        audience: aud, segment: aud === 'SEGMENT' ? segment : 'ALL',
        send_email_template: emailReady,
      });
      toast.success(`Sent to ${aud === 'ALL' ? 'all clients' : segment} — in-app push delivered`);
      setTitle(''); setBody(''); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const copyEmail = (a) => {
    const html = buildEmailHtml(a);
    navigator.clipboard.writeText(html).then(() => toast.success('Email HTML copied to clipboard — paste into your email tool'));
  };

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='mb-5'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Marketing · Campaigns</div>
        <h1 className='font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1 flex items-center gap-2'>
          <Megaphone className='w-6 h-6 text-[#1B3A87] dark:text-[#93C5FD]' /> In-App Push & Email Blasts
        </h1>
        <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>
          Push a notification to all clients or a segment instantly. SMS (Twilio) coming once credentials are added.
        </p>
      </div>

      <div className='grid lg:grid-cols-[1fr_1.1fr] gap-4'>
        <Card className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900' data-testid='campaign-composer'>
          <h2 className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mb-3'>New campaign</h2>
          <div className='space-y-3'>
            <div>
              <Label>Title *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder='August Newsletter' data-testid='campaign-title' />
            </div>
            <div>
              <Label>Body *</Label>
              <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder='What is happening this month?…' data-testid='campaign-body' />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>CTA label</Label>
                <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} data-testid='campaign-cta-label' />
              </div>
              <div>
                <Label>CTA url</Label>
                <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid='campaign-cta-url' />
              </div>
            </div>
            <div>
              <Label>Audience</Label>
              <Select value={segment} onValueChange={(v) => { setSegment(v); setAudience(v === 'ALL' ? 'ALL' : 'SEGMENT'); }}>
                <SelectTrigger className='mt-1.5' data-testid='campaign-segment'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className='flex items-center gap-2 text-sm cursor-pointer'>
              <Checkbox checked={emailReady} onCheckedChange={(v) => setEmailReady(!!v)} data-testid='campaign-email-toggle' />
              <Mail className='w-4 h-4 text-slate-500' />
              Generate email-ready HTML (copy-paste into your mailer)
            </label>
            <Button onClick={send} disabled={sending} className='w-full bg-[#C8102E] hover:bg-[#A50D26] text-white' data-testid='campaign-send-button'>
              {sending ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <Send className='w-4 h-4 mr-2' />}
              {sending ? 'Sending…' : 'Send in-app push'}
            </Button>
          </div>
        </Card>

        <Card className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
          <h2 className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mb-3'>Recent campaigns</h2>
          <div className='space-y-3 max-h-[70vh] overflow-y-auto'>
            {list.length === 0 ? (
              <div className='text-sm text-slate-500'>No campaigns yet.</div>
            ) : list.map(a => (
              <div key={a.id} className='p-3 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700' data-testid={`campaign-row-${a.id}`}>
                <div className='flex items-center gap-2 flex-wrap mb-1'>
                  <Badge className='bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD] border-0 text-[10px]'>{a.audience}{a.segment !== 'ALL' ? ` · ${a.segment}` : ''}</Badge>
                  <Badge variant='outline' className='text-[10px] border-slate-300 dark:border-slate-600'>{a.delivered_count} delivered · {a.read_count} read</Badge>
                </div>
                <div className='font-semibold text-sm text-slate-900 dark:text-slate-50'>{a.title}</div>
                <div className='text-xs text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-wrap line-clamp-3'>{a.body}</div>
                <div className='text-[11px] text-slate-500 mt-1 flex items-center justify-between'>
                  <span>{new Date(a.created_at).toLocaleString()}</span>
                  {a.send_email_template && (
                    <button onClick={() => copyEmail(a)} className='inline-flex items-center gap-1 text-[#1B3A87] dark:text-[#93C5FD] hover:underline' data-testid={`campaign-copy-${a.id}`}>
                      <Copy className='w-3 h-3' /> Copy email HTML
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
